import React, { useState } from 'react';
import { Vehicle } from '@/services/api/vehicles';
import { getBusinessUnitLabel } from '@/types/common';

interface ReturnVehicleDetailViewProps {
  initialData: Vehicle;
}

// Vehicle condition enum mapping
const vehicleConditionMap = {
  GOOD: 'Good',
  MINOR_DAMAGE: 'Minor Damage',
  MAJOR_DAMAGE: 'Major Damage',
} as const;

// Return reason enum mapping
const returnReasonMap = {
  CONTRACT_END: 'Contract End',
  IDLE_AT_HUB_TOO_LONG: 'Idle at Hub for Too Long',
  VEHICLE_SWAP: 'Vehicle Swap',
  CLIENT_REQUEST: 'Client Request',
  OTHER: 'Other',
} as const;

const ReturnVehicleDetailView: React.FC<ReturnVehicleDetailViewProps> = ({
  initialData,
}) => {
  const [selectedTab, setSelectedTab] = useState<string>('details');

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 flex-shrink-0">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => setSelectedTab('details')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              selectedTab === 'details'
                ? 'border-[#5E2AAC]'
                : 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-300'
            }`}
          >
            Vehicle Details
          </button>
          <button
            onClick={() => setSelectedTab('return')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              selectedTab === 'return'
                ? 'border-[#5E2AAC]'
                : 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-300'
            }`}
          >
            Return Info
          </button>
        </nav>
      </div>

      {/* Detail View Content */}
      <div className="flex-1 overflow-y-auto">
        {selectedTab === 'details' ? (
          <div>
            <div className="px-6">
              <div className="pt-6 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Hub Location</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.hubName || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Business Unit</p>
                    <p className="text-sm font-medium text-black">
                      {getBusinessUnitLabel((initialData as any)?.businessUnit)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Plate Number</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.platNomor || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">OEM Name</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.oemName || '-'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Model Name</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.modelName || '-'}
                    </p>
                  </div>
                  <div></div>
                </div>
              </div>
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Nomor Rangka / VIN</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.vin || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Nomor Mesin</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.imei || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="bg-gray-50 px-6 py-2 font-medium heading-field mt-4"
              style={{ fontSize: '12px' }}
            >
              Inspection & Checklist
            </div>
            <div className="px-6">
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Vehicle Condition</p>
                <p className="text-sm font-medium text-black">
                  {(initialData as any)?.returnCondition
                    ? vehicleConditionMap[
                        (initialData as any)
                          .returnCondition as keyof typeof vehicleConditionMap
                      ] || (initialData as any).returnCondition
                    : '-'}
                </p>
              </div>
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">List Of Issues/Damage</p>
                <p className="text-sm font-medium text-black whitespace-pre-wrap break-words">
                  {(initialData as any)?.returnIssues || '-'}
                </p>
              </div>
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Foto Kendaraan</p>
                <div className="w-full h-48 rounded-lg border border-gray-200 flex items-center justify-center mt-2 overflow-hidden">
                  {(initialData as any)?.vehicleHandoverImageURL ||
                  (initialData as any)?.vehicleImageURL ? (
                    <img
                      src={
                        (initialData as any)?.vehicleImageURL ||
                        (initialData as any)?.vehicleHandoverImageURL
                      }
                      alt="Vehicle"
                      className="w-full h-full object-cover bg-gray-100"
                    />
                  ) : (
                    <div className="text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="mt-2 text-sm text-gray-500">
                        No image uploaded
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Equipment Checklist</p>
                <div className="space-y-1 mt-1">
                  {(initialData as any)?.returnUnitEquipment?.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1">
                      {(initialData as any)?.returnUnitEquipment?.map(
                        (equipment: string) => (
                          <li
                            key={equipment}
                            className="text-sm font-medium text-black"
                          >
                            {equipment}
                          </li>
                        )
                      )}
                    </ul>
                  ) : (
                    <p className="text-sm font-medium text-black">-</p>
                  )}
                </div>
              </div>
            </div>

            <div
              className="bg-gray-50 px-6 py-2 font-medium heading-field mt-4"
              style={{ fontSize: '12px' }}
            >
              Info
            </div>
            <div className="px-6">
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Created By</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.createdBy || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Created Date</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.createdAt
                        ? new Date(
                            (initialData as any).createdAt
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).createdAt
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 pb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Last Updated By</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.updatedBy || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Last Updated</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.updatedAt
                        ? new Date(
                            (initialData as any).updatedAt
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).updatedAt
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div
              className="bg-gray-50 px-6 py-2 font-medium heading-field"
              style={{ fontSize: '12px' }}
            >
              Return Information
            </div>
            <div className="px-6">
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Odometer at Return</p>
                <p className="text-sm font-medium text-black">
                  {(initialData as any)?.returnOdometer
                    ? `${(initialData as any).returnOdometer} km`
                    : '-'}
                </p>
              </div>

              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Contract Start Date</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.contractStartDate
                        ? new Date(
                            (initialData as any).contractStartDate
                          ).toLocaleDateString('en-GB')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Return Date</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.returnDate
                        ? new Date(
                            (initialData as any).returnDate
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).returnDate
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <p className="heading-field mb-1">Return Reason</p>
                <p className="text-sm font-medium text-black">
                  {(initialData as any)?.returnReason
                    ? returnReasonMap[
                        (initialData as any)
                          .returnReason as keyof typeof returnReasonMap
                      ] || (initialData as any).returnReason
                    : '-'}
                </p>
              </div>

              {(initialData as any)?.returnReasonOther && (
                <div className="pt-4 pb-4">
                  <p className="heading-field mb-1">Other Reason Details</p>
                  <p className="text-sm font-medium text-black whitespace-pre-wrap">
                    {(initialData as any)?.returnReasonOther}
                  </p>
                </div>
              )}
            </div>

            <div
              className="bg-gray-50 px-6 py-2 font-medium heading-field mt-4"
              style={{ fontSize: '12px' }}
            >
              Return Documentation
            </div>

            <div className="px-6">
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Document BAST</p>
                <p className="text-sm font-medium text-black break-words">
                  {(initialData as any)?.returnBastURL
                    ? `${(initialData as any).returnBastURL}`
                    : '-'}
                </p>
              </div>

              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Nama Rep OEM</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.returnOemRepName
                        ? `${(initialData as any).returnOemRepName}`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Nomor Rep OEM</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.returnOemRepPhone
                        ? `${(initialData as any).returnOemRepPhone}`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <p className="heading-field mb-1">Notes</p>
                <p className="text-sm font-medium text-black break-words whitespace-pre-wrap">
                  {(initialData as any)?.returnNotes
                    ? `${(initialData as any).returnNotes}`
                    : '-'}
                </p>
              </div>
            </div>

            <div
              className="bg-gray-50 px-6 py-2 font-medium heading-field mt-4"
              style={{ fontSize: '12px' }}
            >
              Info
            </div>
            <div className="px-6">
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Created By</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.createdBy || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Created Date</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.createdAt
                        ? new Date(
                            (initialData as any).createdAt
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).createdAt
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 pb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Last Updated By</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.updatedBy || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Last Updated</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.updatedAt
                        ? new Date(
                            (initialData as any).updatedAt
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).updatedAt
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnVehicleDetailView;
